import type { RouteEtaData } from './RouteEta';
import type { MoveRisk } from './types';

export interface RouteCascadeEstimate {
  origin: string;
  destination: string;
  originTimezone: string;
  destinationTimezone: string;
  target: 'origin' | 'destination';
  updatedAt: string;
  anchorPronumber: number;
  anchorLegnum: number;
  anchorRouteId?: string | null;
  awaitingUpdate?: boolean;
}

const ACTIVE = new Set(['In Transit', 'At Origin', 'At Destination', 'Started']);
const key = (move: MoveRisk) => `${move.pronumber}-${move.legnum}`;
const minutes = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value) && value >= 0;
type CascadeAnchor = { driver: string; arrival: number; stop: number; updated: string; pro: number; leg: number; routeId?: string | null; awaitingUpdate?: boolean };

/** Shared with the original ETA: active moves first, then dispatch priority. */
export function orderProjectionMoves(moves: MoveRisk[]): MoveRisk[] {
  const sort = (a: MoveRisk, b: MoveRisk) => {
    const aP = a.dispatch_priority && a.dispatch_priority > 0 ? a.dispatch_priority : 999;
    const bP = b.dispatch_priority && b.dispatch_priority > 0 ? b.dispatch_priority : 999;
    return aP - bP || a.pronumber - b.pronumber || a.legnum - b.legnum;
  };
  return [...moves.filter(m => ACTIVE.has(m.status)).sort(sort),
    ...moves.filter(m => m.status === 'Dispatched').sort(sort)];
}

export function routeCheckIsFresh(updatedAt: string | null | undefined, now: Date): boolean {
  const checked = updatedAt ? Date.parse(updatedAt) : NaN;
  const age = now.getTime() - checked;
  return Number.isFinite(age) && age >= -60_000 && age <= 10 * 60_000;
}

function liveArrival(data: RouteEtaData | null | undefined, now: Date): number | null {
  if (data?.status !== 'en route' || !routeCheckIsFresh(data.updated_at, now) || !data.eta) return null;
  // API timestamps are UTC/offset-bearing; never interpret a wall clock as UTC.
  if (!/(?:Z|[+-]\d{2}:\d{2})$/.test(data.eta)) return null;
  const instant = Date.parse(data.eta);
  // Keep the last reported instant between polls even if it has just passed.
  // The check-time freshness limit still applies; do not slide it to "now".
  return Number.isFinite(instant) ? instant : null;
}

function predatesMove(instant: number, plannedDay: string | undefined, zone: string): boolean {
  if (!plannedDay || !/^\d{4}-\d{2}-\d{2}$/.test(plannedDay)) return false;
  return new Date(instant).toLocaleDateString('en-CA', { timeZone: zone }) < plannedDay;
}

/** A separate UTC cascade; never feeds CMS ETA math, risk, or Samsara schedules. */
export function computeRouteCascade(moves: MoveRisk[], now = new Date()): Map<string, RouteCascadeEstimate> {
  const result = new Map<string, RouteCascadeEstimate>();
  let anchor: CascadeAnchor | null = null;
  let previous: MoveRisk | null = null;
  for (const move of orderProjectionMoves(moves)) {
    const data = move.route_eta;
    const stop = data?.stop_minutes;
    const direct = move.status === 'In Transit' ? liveArrival(data, now) : null;
    const arrived = move.status === 'At Destination' && data?.status === 'arrived' && routeCheckIsFresh(data.updated_at, now);
    if ((direct !== null || arrived) && minutes(stop)) {
      anchor = { driver: move.driver, arrival: direct ?? now.getTime(),
        stop: arrived ? Math.max(0, stop - (minutes(move.sitting_minutes) ? move.sitting_minutes : 0)) : stop,
        updated: data!.updated_at!, pro: move.pronumber, leg: move.legnum, routeId: data!.route_id,
        awaitingUpdate: direct !== null && direct < now.getTime() };
    } else if (anchor && previous && data && move.status === 'Dispatched' && move.driver === anchor.driver && minutes(stop)) {
      const base: CascadeAnchor = anchor;
      const drive = minutes(move.route_duration_minutes) ? move.route_duration_minutes : move.estimated_minutes;
      if (!minutes(drive) || (drive === 0 && !(move.origcode > 0 && move.origcode === move.destcode))) {
        anchor = null; previous = move; continue;
      }
      const sameStop = previous.destcode > 0 && previous.destcode === move.origcode;
      const linked = sameStop && previous.pronumber === move.pronumber;
      // Prefer the exact location pair, independent of API/display ordering.
      // The legacy scalar belongs only to the API's original adjacent pair.
      const originalNext = moves[moves.indexOf(previous) + 1];
      const pairTravel = previous.route_eta?.travel_minutes_by_origin?.[move.origcode];
      const deadhead = sameStop ? 0 : minutes(pairTravel) ? pairTravel
        : originalNext && key(originalNext) === key(move) && minutes(previous.travel_to_next_minutes)
        ? previous.travel_to_next_minutes : null;
      if (deadhead === null) {
        anchor = null; previous = move; continue;
      }
      const origin = sameStop ? base.arrival : base.arrival + (base.stop + deadhead) * 60_000;
      const departure = base.arrival + (base.stop + deadhead) * 60_000;
      const destination = departure + drive * 60_000;
      const originTimezone = data.origin_timezone || 'America/Chicago';
      const destinationTimezone = data.timezone || 'America/Chicago';
      try {
        if (predatesMove(origin, move.origapptdate, originTimezone) || predatesMove(destination, move.destapptdate, destinationTimezone)) {
          anchor = null; previous = move; continue;
        }
        result.set(key(move), { origin: new Date(origin).toISOString(), destination: new Date(destination).toISOString(),
          originTimezone, destinationTimezone, target: linked ? 'destination' : 'origin',
          updatedAt: base.updated, anchorPronumber: base.pro, anchorLegnum: base.leg, anchorRouteId: base.routeId,
          awaitingUpdate: base.awaitingUpdate });
        anchor = { ...base, arrival: destination, stop };
      } catch { anchor = null; }
    } else {
      anchor = null;
    }
    previous = move;
  }
  return result;
}
